import * as XLSX from 'xlsx';
import Papa from 'papaparse';

/**
 * Rules to automatically detect column names in imported files
 */
const AUTO_MAPPING_RULES = {
  fullName: [
    'full name', 'fullname', 'name', 'patient name', 'patient', 'ονοματεπωνυμο',
    'ονοματεπώνυμο', 'ονομα επωνυμο', 'όνομα επώνυμο', 'ασθενης', 'ασθενής',
    'ονομα ασθενους', 'όνομα ασθενούς', 'επωνυμο ονομα', 'επώνυμο όνομα', 'names'
  ],
  lastName: [
    'last name', 'lastname', 'surname', 'family name', 'επωνυμο', 'επώνυμο',
    'last_name', 'epitheto'
  ],
  firstName: [
    'first name', 'firstname', 'given name', 'ονομα', 'όνομα',
    'first_name', 'onoma'
  ],
  amka: [
    'amka', 'αμκα', 'ssn', 'social security', 'social security number', 'amka number'
  ],
  phone: [
    'phone', 'mobile', 'telephone', 'τηλεφωνο', 'τηλέφωνο', 'κινητο', 'κινητό',
    'τηλ', 'τηλ.', 'cell'
  ],
  dob: [
    'dob', 'date of birth', 'date_of_birth', 'birthdate', 'birth date',
    'ημερομηνια γεννησης', 'ημερομηνία γέννησης', 'ημ. γεννησης', 'ημ. γέννησης'
  ],
};

/**
 * Parses an Excel (.xlsx, .xls) or CSV file into structured headers and rows.
 * 
 * @param {File} file - The file to parse
 * @returns {Promise<{ headers: string[], rows: object[], rawCount: number }>}
 */
export const parseSpreadsheetFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      return reject(new Error('No file provided'));
    }

    const extension = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();

    if (extension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: 'greedy',
        encoding: 'UTF-8',
        complete: (results) => {
          try {
            const headers = (results.meta.fields || []).map(h => String(h).trim()).filter(Boolean);
            const rows = results.data.filter(row => {
              return Object.values(row).some(v => v !== null && v !== undefined && String(v).trim().length > 0);
            });
            resolve({ headers, rows, rawCount: rows.length });
          } catch (err) {
            reject(err);
          }
        },
        error: (err) => {
          reject(new Error(`CSV Parsing error: ${err.message}`));
        }
      });
    } else if (extension === 'xlsx' || extension === 'xls') {
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            return reject(new Error('Spreadsheet has no sheets'));
          }

          const worksheet = workbook.Sheets[firstSheetName];
          const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });

          if (rawJson.length === 0) {
            // Check if there are headers only
            const headerArray = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
            return resolve({
              headers: headerArray.map(h => String(h).trim()).filter(Boolean),
              rows: [],
              rawCount: 0
            });
          }

          const headers = Object.keys(rawJson[0]).map(h => String(h).trim()).filter(Boolean);
          const rows = rawJson.filter(row => {
            return Object.values(row).some(v => v !== null && v !== undefined && String(v).trim().length > 0);
          });

          resolve({ headers, rows, rawCount: rows.length });
        } catch (err) {
          reject(new Error(`Excel Parsing error: ${err.message}`));
        }
      };

      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error('Unsupported file type. Please upload a .xlsx, .xls, or .csv file.'));
    }
  });
};

/**
 * Automatically inspects headers and suggests column mappings.
 * 
 * @param {string[]} headers 
 * @returns {object} mapping object e.g. { fullName: 'Name', amka: 'AMKA' }
 */
export const autoDetectColumnMapping = (headers = []) => {
  const mapping = {
    mode: 'fullName', // 'fullName' or 'separateNames'
    fullName: '',
    lastName: '',
    firstName: '',
    amka: '',
    phone: '',
    dob: '',
  };

  const cleanHeaders = headers.map(h => ({
    original: h,
    lower: h.toLowerCase().trim()
  }));

  // 1. Check for Full Name column
  const fullNameMatch = cleanHeaders.find(h => 
    AUTO_MAPPING_RULES.fullName.some(rule => h.lower === rule || h.lower.includes(rule))
  );
  if (fullNameMatch) {
    mapping.fullName = fullNameMatch.original;
    mapping.mode = 'fullName';
  }

  // 2. Check for Last Name and First Name columns
  const lastNameMatch = cleanHeaders.find(h => 
    AUTO_MAPPING_RULES.lastName.some(rule => h.lower === rule || h.lower.includes(rule))
  );
  const firstNameMatch = cleanHeaders.find(h => 
    AUTO_MAPPING_RULES.firstName.some(rule => h.lower === rule || h.lower.includes(rule))
  );

  if (lastNameMatch) {
    mapping.lastName = lastNameMatch.original;
  }
  if (firstNameMatch) {
    mapping.firstName = firstNameMatch.original;
  }

  if (lastNameMatch && firstNameMatch && !fullNameMatch) {
    mapping.mode = 'separateNames';
  }

  // 3. Check for AMKA
  const amkaMatch = cleanHeaders.find(h => 
    AUTO_MAPPING_RULES.amka.some(rule => h.lower === rule || h.lower.includes(rule))
  );
  if (amkaMatch) mapping.amka = amkaMatch.original;

  // 4. Check for Phone
  const phoneMatch = cleanHeaders.find(h => 
    AUTO_MAPPING_RULES.phone.some(rule => h.lower === rule || h.lower.includes(rule))
  );
  if (phoneMatch) mapping.phone = phoneMatch.original;

  // 5. Check for DOB
  const dobMatch = cleanHeaders.find(h => 
    AUTO_MAPPING_RULES.dob.some(rule => h.lower === rule || h.lower.includes(rule))
  );
  if (dobMatch) mapping.dob = dobMatch.original;

  // Fallback: if nothing matched at all, map first column as fullName
  if (!mapping.fullName && !mapping.lastName && headers.length > 0) {
    mapping.fullName = headers[0];
    mapping.mode = 'fullName';
  }

  return mapping;
};

/**
 * Sends mapped rows to server for batch diagnosis lookup.
 * 
 * @param {object} apiClient - axios instance
 * @param {object[]} rows - raw spreadsheet rows
 * @param {object} mapping - column mapping config
 * @returns {Promise<{ results: object[], summary: object }>}
 */
export const executeBatchLookup = async (apiClient, rows, mapping) => {
  const preparedItems = rows.map((row, index) => {
    let rawName = '';
    let lastName = '';
    let firstName = '';

    if (mapping.mode === 'separateNames') {
      lastName = mapping.lastName ? (row[mapping.lastName] || '') : '';
      firstName = mapping.firstName ? (row[mapping.firstName] || '') : '';
      rawName = [lastName, firstName].filter(Boolean).join(' ');
    } else {
      rawName = mapping.fullName ? (row[mapping.fullName] || '') : '';
    }

    const amka = mapping.amka ? (row[mapping.amka] || '') : '';
    const phone = mapping.phone ? (row[mapping.phone] || '') : '';
    const dob = mapping.dob ? (row[mapping.dob] || '') : '';

    return {
      rowIndex: index + 1,
      rawName: String(rawName).trim(),
      lastName: String(lastName).trim(),
      firstName: String(firstName).trim(),
      amka: String(amka).trim(),
      phone: String(phone).trim(),
      dob: String(dob).trim(),
    };
  });

  const response = await apiClient.post('/patients/batch-diagnosis-lookup', {
    items: preparedItems,
  });

  const resultsWithOriginal = (response.data?.results || []).map((item) => ({
    ...item,
    originalData: rows[item.rowIndex - 1] || item.originalData || {},
  }));

  return {
    ...response.data,
    results: resultsWithOriginal,
  };
};

/**
 * Prepares data objects for export (combining original columns and matched clinical data).
 */
const buildExportData = (results, exportMode = 'enriched') => {
  return results.map(item => {
    const p = item.matchedPatient;
    const baseOriginal = item.originalData || {};

    let statusLabel = 'Not Found';
    if (item.status === 'EXACT_MATCH') statusLabel = 'Found (Exact)';
    else if (item.status === 'MULTIPLE_MATCHES') statusLabel = 'Found (Multiple Matches)';
    else if (item.status === 'FUZZY_MATCH') statusLabel = 'Found (Approximate)';

    if (exportMode === 'clinical_only') {
      return {
        'Row #': item.rowIndex,
        'Imported Search Name': item.rawName,
        'Match Status': statusLabel,
        'Matched Patient Name': p ? p.fullName : '—',
        'Primary Diagnosis': p ? (p.diagnosis || 'None recorded') : '—',
        'Latest Visit Diagnosis': p ? (p.latestVisitDiagnosis || '—') : '—',
        'AMKA': p ? (p.amka || '—') : '—',
        'Date of Birth / Age': p ? (p.date_birth || p.age || '—') : '—',
        'Phone': p ? (p.phone || p.mobile || '—') : '—',
        'Last Visit Date': p ? (p.last_visit || '—') : '—',
      };
    }

    // Default 'enriched' mode: original columns + appended clinical columns
    return {
      ...baseOriginal,
      'EMR_Match_Status': statusLabel,
      'EMR_Matched_Name': p ? p.fullName : '',
      'EMR_Primary_Diagnosis': p ? (p.diagnosis || '') : '',
      'EMR_Latest_Visit_Diagnosis': p ? (p.latestVisitDiagnosis || '') : '',
      'EMR_AMKA': p ? (p.amka || '') : '',
      'EMR_Age': p ? (p.age || '') : '',
      'EMR_Phone': p ? (p.phone || p.mobile || '') : '',
      'EMR_Last_Visit': p ? (p.last_visit || '') : '',
    };
  });
};

/**
 * Exports matched results to an Excel (.xlsx) file.
 * 
 * @param {object[]} results - Array of match items
 * @param {string} exportMode - 'enriched' or 'clinical_only'
 * @param {string} filenamePrefix - base filename
 */
export const exportResultsToExcel = (results, exportMode = 'enriched', filenamePrefix = 'Diagnosis_Lookup') => {
  const exportData = buildExportData(results, exportMode);
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Diagnosis Results');

  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `${filenamePrefix}_${dateStr}.xlsx`;
  XLSX.writeFile(workbook, filename);
};

/**
 * Exports matched results to a CSV file (with UTF-8 BOM for Greek language compatibility).
 * 
 * @param {object[]} results - Array of match items
 * @param {string} exportMode - 'enriched' or 'clinical_only'
 * @param {string} filenamePrefix - base filename
 */
export const exportResultsToCsv = (results, exportMode = 'enriched', filenamePrefix = 'Diagnosis_Lookup') => {
  const exportData = buildExportData(results, exportMode);
  const csvContent = Papa.unparse(exportData);

  // Prepend UTF-8 BOM (\uFEFF) so Excel on Windows/Mac opens Greek text cleanly
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `${filenamePrefix}_${dateStr}.csv`;

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
